import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowRight,
  Sparkles,
  ListChecks,
  BarChart3,
  ShieldCheck,
  FileDown,
  Star,
  Zap,
  Table2,
  Settings2,
  Building2,
  Cpu,
  Server,
  MapPin,
  Mail,
  Antenna,
  ExternalLink,
  ShoppingCart,
} from "lucide-react";
import { motion } from "framer-motion";

const EDITION_PREVIEW = [
  {
    key: "express",
    name: "XProtect Express+",
    subtitle: "Kleine Installationen",
    Icon: Cpu,
    bullets: ["Bis zu 100 Geräte", "Single Server", "Alarm Manager"],
  },
  {
    key: "professional",
    name: "XProtect Professional+",
    subtitle: "Mittelgroß",
    Icon: Server,
    bullets: ["Unbegrenzte Geräte*", "Multi-Server", "Smart Map"],
  },
  {
    key: "expert",
    name: "XProtect Expert",
    subtitle: "Mittel bis groß",
    Icon: ShieldCheck,
    bullets: ["Failover", "OIDC/SSO, Verschlüsselung", "Smart Wall optional"],
  },
  {
    key: "corporate",
    name: "XProtect Corporate",
    subtitle: "KRITIS / verteilt",
    Icon: Building2,
    bullets: ["Evidence Lock", "Federation", "Dashboard, Incident Manager"],
  },
];

const FEATURES = [
  {
    Icon: ListChecks,
    title: "Schritt-für-Schritt Assistent",
    text: "Beantworten Sie einige gezielte Fragen zu Kameras, Standorten, Failover und Sicherheit – der Assistent führt Sie durch.",
  },
  {
    Icon: BarChart3,
    title: "Regelbasierte Empfehlung",
    text: "Alle Antworten werden gewichtet ausgewertet – nicht nur die Kameraanzahl. Mit prozentualer Live-Eignung pro Edition.",
  },
  {
    Icon: Table2,
    title: "Expertenmodus, Konfigurator & Vergleich",
    text: "Direkte Feature-Auswahl mit Ampel-Feedback, freie Lizenz- und Care-Konfiguration mit Xortec-Artikelnummern sowie vollständige Vergleichstabelle.",
  },
  {
    Icon: FileDown,
    title: "PDF- & JSON-Export",
    text: "Empfehlung samt Begründung und Vergleich als PDF mit Xortec-Branding oder strukturiertes JSON exportieren.",
  },
];

const STEPS = [
  { Icon: Sparkles, title: "Projekt beschreiben", text: "Kameras, Standorte, Failover, Cyber-Security, Kritikalität." },
  { Icon: Star, title: "Empfehlung erhalten", text: "Passende Edition mit klarer Begründung – warum diese und keine andere." },
  { Icon: FileDown, title: "Exportieren & teilen", text: "PDF für Kunden, JSON für CRM – lokal speichern und wieder öffnen." },
];

export default function LandingPage({ onStart, onExpert, onCompare, onContact, onCare, onConfigurator }) {
  return (
    <div data-testid="landing-page" className="space-y-16 md:space-y-24 pb-8">
      {/* HERO */}
      <section className="relative pt-8 md:pt-14">
        <div className="relative max-w-[1200px] mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-6"
          >
            <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs text-white/80">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
              Ein Werkzeug der Xortec GmbH
            </div>

            <div className="flex items-center justify-center pt-2">
              <img
                src="/xortec/wordmark-white.webp"
                alt="XORTEC"
                className="h-14 md:h-20 lg:h-24 object-contain drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)]"
              />
            </div>

            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl text-on-wave">
              Die richtige
              <span className="text-primary"> Milestone XProtect</span>
              <br className="hidden md:block" />
              in wenigen Minuten finden.
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl text-on-wave-soft">
              Der Auswahlassistent von Xortec führt Facherrichter, Integratoren und Planer strukturiert durch
              relevanten Fragen und empfiehlt die passende Milestone XProtect Edition.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <Button
                data-testid="cta-start"
                size="lg"
                onClick={onStart}
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-base h-12 px-6"
              >
                Assistent starten
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="cta-expert"
                size="lg"
                variant="outline"
                onClick={onExpert}
                className="h-12 px-6 text-base border-border"
              >
                <Settings2 className="w-4 h-4 mr-2" />
                Expertenmodus
              </Button>
              {onConfigurator && (
                <Button
                  data-testid="cta-configurator"
                  size="lg"
                  variant="outline"
                  onClick={onConfigurator}
                  className="h-12 px-6 text-base border-border"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Konfigurator
                </Button>
              )}
              <Button
                data-testid="cta-compare"
                size="lg"
                variant="ghost"
                onClick={onCompare}
                className="h-12 px-6 text-base text-muted-foreground hover:text-foreground"
              >
                <Table2 className="w-4 h-4 mr-2" />
                Vergleichstabelle
              </Button>
              {onCare && (
                <Button
                  data-testid="cta-care"
                  size="lg"
                  variant="ghost"
                  onClick={onCare}
                  className="h-12 px-6 text-base text-muted-foreground hover:text-foreground"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Care-Pakete
                </Button>
              )}
              <Button
                data-testid="cta-contact"
                size="lg"
                variant="ghost"
                onClick={onContact}
                className="h-12 px-6 text-base text-muted-foreground hover:text-foreground"
              >
                <Mail className="w-4 h-4 mr-2" />
                Kontakt
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-8 pt-8 max-w-md w-full text-center">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-primary">4</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Editionen</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-primary">15</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Fragen</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-primary">30+</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Vergleichsmerkmale</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2 text-on-wave-soft">Warum dieses Tool</div>
          <h2 className="text-2xl md:text-3xl font-bold text-on-wave">Verlässlich empfehlen – nicht nur nach Kamerazahl</h2>
          <p className="text-white/80 mt-2 max-w-2xl mx-auto text-on-wave-soft">
            Wir bewerten alle Anforderungen und erklären jeden Schritt der Empfehlung nachvollziehbar.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map(({ Icon, title, text }) => (
            <Card key={title} className="p-5 bg-card/70 border-border card-elev tile-hover">
              <div className="w-11 h-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center mb-3">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Editions preview */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2 text-on-wave-soft">Die vier Editionen</div>
          <h2 className="text-2xl md:text-3xl font-bold text-on-wave">Von Express+ bis Corporate</h2>
          <p className="text-white/80 mt-2 max-w-2xl mx-auto text-on-wave-soft">
            Der Assistent wählt die passende Edition anhand Ihrer Anforderungen.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EDITION_PREVIEW.map(({ key, name, subtitle, Icon, bullets }) => (
            <Card
              key={key}
              className="p-5 bg-card/70 border-border card-elev tile-hover flex flex-col"
              data-testid={`landing-edition-${key}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{subtitle}</div>
                  <div className="font-semibold">{name}</div>
                </div>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1.5 mt-1">
                {bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <span className="text-primary">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STEPS.map(({ Icon, title, text }, i) => (
            <div key={title} className="rounded-xl border border-border bg-card/70 p-6 tile-hover">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Kameramast Section */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="rounded-2xl border border-border bg-card/70 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr]">
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2 flex items-center gap-2">
                <Antenna className="w-3.5 h-3.5" />
                Xortec Kameramasten
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
                Software allein ist die halbe Miete. Die passende <span className="text-primary">Kameramastlösung</span> von Xortec.
              </h2>
              <p className="text-muted-foreground mb-5 leading-relaxed">
                Ergänzend zur XProtect-Empfehlung liefert Xortec eigene Kameramasten in unterschiedlichen Höhen und
                Bauformen – ideal für Parkplätze, Perimeter, Industrieflächen und Baustellen.
                Robuste Konstruktionen, blitzschutzgerecht, montage- und wartungsfreundlich.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1.5 mb-6">
                <li className="flex gap-2"><span className="text-primary">•</span>Mastvarianten von 4 m bis 12 m Höhe</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Multi-Kamera-Aufsätze und PTZ-Halterungen</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Verkabelung, Blitzschutz und Netzeinspeisung ab Werk</li>
                <li className="flex gap-2"><span className="text-primary">•</span>Passend für Hikvision, Hanwha, Avigilon, Bosch, Pelco</li>
              </ul>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" className="border-primary/40 text-foreground">
                  <a
                    data-testid="cta-kameramast-brochure"
                    href="https://xortec.de/media/23/25/72/1758812184/Xortec%20Kameramasten%20Broschre.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Broschüre ansehen
                    <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </a>
                </Button>
                <Button
                  data-testid="cta-kameramast-contact"
                  onClick={onContact}
                  className="bg-primary text-primary-foreground"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Beratung anfragen
                </Button>
              </div>
            </div>
            <div className="relative bg-gradient-to-br from-primary/15 to-secondary/40 flex items-center justify-center p-8 border-t md:border-t-0 md:border-l border-border">
              <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
              <div className="relative flex flex-col items-center gap-4 text-center">
                <img
                  src="/xortec/kameramasten-white.webp"
                  alt="Xortec Kameramasten"
                  className="h-16 md:h-20 object-contain drop-shadow-[0_4px_18px_rgba(0,0,0,0.5)]"
                />
                <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
                  {[
                    { h: "4–6 m", label: "Standard" },
                    { h: "6–8 m", label: "Mid-Range" },
                    { h: "8–12 m", label: "Perimeter" },
                  ].map((t) => (
                    <div key={t.h} className="rounded-md border border-border bg-background/60 py-2">
                      <div className="text-sm font-bold text-primary">{t.h}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.label}</div>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground max-w-xs">
                  Individuelle Konfiguration nach Kameraanzahl, Windlast und Ausleuchtung.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 via-primary/10 to-transparent p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Jetzt starten</div>
            <h3 className="text-2xl md:text-3xl font-bold mb-1">In wenigen Minuten zur passenden XProtect Edition.</h3>
            <p className="text-muted-foreground">Keine Registrierung. Ergebnisse lokal speichern und als PDF/JSON exportieren.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="cta-start-bottom"
              size="lg"
              onClick={onStart}
              className="bg-primary hover:bg-primary/90 text-primary-foreground text-base h-12 px-6"
            >
              <Zap className="w-4 h-4 mr-2" />
              Assistent starten
            </Button>
            <Button
              data-testid="cta-contact-bottom"
              size="lg"
              variant="outline"
              onClick={onContact}
              className="h-12 px-6 text-base"
            >
              <Mail className="w-4 h-4 mr-2" />
              Kontakt aufnehmen
            </Button>
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-xs">
          {[
            { city: "Frankfurt", note: "Hauptsitz" },
            { city: "Berlin", note: "Niederlassung" },
            { city: "Hamburg", note: "Niederlassung" },
            { city: "München", note: "Niederlassung" },
            { city: "Regensburg", note: "Niederlassung" },
          ].map((c) => (
            <div key={c.city} className="rounded-lg border border-border bg-secondary/30 py-3">
              <MapPin className="w-3.5 h-3.5 text-primary mx-auto mb-1" />
              <div className="font-semibold text-foreground">{c.city}</div>
              <div className="text-muted-foreground">{c.note}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
